import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { sendSlackMessage } from "@/lib/sendSlackMessage";

interface Step8AiSuggestionsProps {
  isLoading: boolean;
  sessionUrl: string | null;
  error: boolean;
  onClose: () => void;
  contactEmail: string;
  participantsCount: string;
  budget: string;
  eventType: string;
  location: string;
}

const steps = [
  {
    label: "Recherche de lieux d'exception...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75",
  },
  {
    label: "Analyse de vos préférences...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75",
  },
  {
    label: "Sélection des meilleurs sites...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75",
  },
  {
    label: "Comparaison des options...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75",
  },
  {
    label: "Finalisation des suggestions...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75",
  },
  {
    label: "Presque prêt...",
    image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75",
  },
];

const previewImages = [
  "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75",
  "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75",
  "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75",
];

const Step8AiSuggestions: React.FC<Step8AiSuggestionsProps> = ({
  isLoading,
  sessionUrl,
  error,
  onClose,
  contactEmail,
  participantsCount,
  budget,
  eventType,
  location,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setStepIndex((prev) => (prev + 1) % steps.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && sessionUrl && !error) {
      const timer = setTimeout(() => setShowSuccess(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, sessionUrl, error]);

  const handleViewSuggestions = async () => {
    if (!sessionUrl) return;
    window.open(sessionUrl, "_blank");
    try {
      const slackMessage = `🤖 *AI Suggestions viewed*\n*Email* : ${contactEmail}\n*Event type* : ${eventType}\n*Participants* : ${participantsCount} personnes\n*Budget* : ${budget}\n*Location* : ${location || "Non spécifié"}\n*Session URL* : ${sessionUrl}`;
      const slackChannel = contactEmail === "test@naboo.app" ? "C09TA10FMU5" : "C0APWJ9KJQ5";
      await sendSlackMessage(slackMessage, slackChannel);
    } catch (err) {
      console.error("Failed to send Slack notification:", err);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center h-full">
        <div className="rounded-full bg-green-100 p-3 mb-6">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h3 className="text-2xl font-normal mb-4">Votre demande a bien été envoyée !</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          Notre équipe vous contactera rapidement avec des suggestions personnalisées.
        </p>
        <Button onClick={onClose} className="rounded-xl bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground font-semibold px-4 py-2">
          Fermer
        </Button>
      </div>
    );
  }

  if (isLoading) {
    const progress = ((stepIndex + 1) / steps.length) * 100;
    return (
      <div className="flex flex-col items-center justify-center px-4 text-center h-full">
        <div className="relative w-full max-w-md h-64 rounded-2xl overflow-hidden mb-4">
          {steps.map((step, i) => (
            <img
              key={i}
              src={step.image}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                i === stepIndex ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4">
            {steps.map((step, i) => (
              <p
                key={i}
                className={`absolute bottom-4 left-4 right-4 text-sm font-medium text-white transition-opacity duration-500 ${
                  i === stepIndex ? "opacity-100" : "opacity-0"
                }`}
              >
                {step.label}
              </p>
            ))}
          </div>
        </div>

        <div className="w-full max-w-md">
          <div className="h-1 w-full rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full bg-cta-green transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className={`flex flex-col items-center justify-center px-4 text-center h-full transition-all duration-500 ${showSuccess ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
      <div className="grid grid-cols-3 gap-2 w-full max-w-sm mb-6">
        {previewImages.map((src, i) => (
          <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      <h3 className="text-xl md:text-2xl font-semibold mb-2">Vos suggestions sont prêtes 🎉</h3>
      <p className="text-muted-foreground mb-6 max-w-sm text-sm">
        Nous avons sélectionné des lieux qui correspondent parfaitement à votre événement.
      </p>
      <Button
        onClick={handleViewSuggestions}
        className="font-semibold px-4 py-2 rounded-xl bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:scale-105 transition-all duration-300"
      >
        Voir mes suggestions
      </Button>
    </div>
  );
};

export default Step8AiSuggestions;
